import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { StanButton } from '@/components';
import { cn } from '@/lib';
import { useStanRouter } from '@/hooks/useStanRouter';
import { EmailRow } from './EmailRow';
import { TriggerDisplay } from './TriggerDisplay';
import type { FlowEmailTimelineProps } from './FlowEmailTimeline.types';
import styles from './FlowEmailTimeline.module.css';

import { LuPlus } from 'react-icons/lu';
import { useFlowEmailTimeline } from './hooks/useFlowEmailTimeline';

export const FlowEmailTimeline = React.memo<FlowEmailTimelineProps>(
    ({
        flowEmails,
        flowId,
        flowStatus,
        flowTrigger,
        flow,
        hasNewReferralTag = false,
        compact = false,
        currentEmailId,
        onNavigateToEmail,
        onAddEmail,
    }) => {
        const { push } = useStanRouter();
        const [searchParams] = useSearchParams();
        const { user } = useUser();
        const currency = user?.data?.currency || 'USD';

        const { triggerText, blockAddingEmail, emailAnalytics } = useFlowEmailTimeline({
            flow,
            flowTrigger,
            flowEmails,
            hasNewReferralTag,
            currency,
            compact,
        });

        const handleEmailClick = (emailId: number) => {
            if (onNavigateToEmail) {
                onNavigateToEmail(emailId);
            } else {
                push(`/flows/${flowId}/emails/${emailId}`);
            }
        };

        const handleAddEmailClick = () => {
            if (onAddEmail) {
                onAddEmail();
            } else {
                const triggerType = searchParams.get('triggerType') || flowTrigger;
                push(`/flows/${flowId}/new?triggerType=${triggerType}`);
            }
        };

        return (
            <div className='relative -mt-2'>
                <div
                    className={cn(
                        'border-neutral-subtle relative pt-2 max-md:border-t',
                        flowEmails.length === 0 && '',
                        styles.timelineContainer,
                        compact && styles.compact
                    )}
                >
                    <TriggerDisplay triggerText={triggerText} triggerType={flowTrigger} compact={compact} />

                    {flowEmails.map(email => {
                        const analytics = emailAnalytics[email.flow_email_id];
                        if (!analytics) return null;

                        const isActive = compact && currentEmailId === email.flow_email_id;

                        return (
                            <EmailRow
                                key={email.flow_email_id}
                                email={email}
                                flowStatus={flowStatus}
                                analytics={analytics}
                                onEmailClick={() => handleEmailClick(email.flow_email_id)}
                                compact={compact}
                                isActive={isActive}
                            />
                        );
                    })}

                    {/* Show "New Email" row when in compact mode and no currentEmailId (creating new email) */}
                    {compact && !currentEmailId && (
                        <EmailRow
                            key='new-email'
                            email={{
                                flow_email_id: 0,
                                flow_id: flowId,
                                status: 'draft',
                                delay: 0,
                                data: {
                                    subject: 'New Email',
                                    body: '',
                                    delay: 0,
                                },
                            }}
                            flowStatus={flowStatus}
                            analytics={{
                                sales: '-',
                                sent: '-',
                                openRate: '-',
                                clickRate: '-',
                            }}
                            onEmailClick={() => {}}
                            compact={compact}
                            isActive
                        />
                    )}

                    <div className={cn('relative z-10', compact ? 'pl-8' : 'pl-10 md:pt-1.5')}>
                        <StanButton
                            name='add-email-button'
                            variant='outline'
                            size='xs'
                            type='button'
                            onClick={handleAddEmailClick}
                            disabled={blockAddingEmail || (compact && !currentEmailId)}
                            className='not-disabled:not-hover:bg-canvas!'
                        >
                            <span className='flex items-center gap-1'>
                                <LuPlus />
                                <span>Add Email</span>
                            </span>
                        </StanButton>
                    </div>
                </div>
            </div>
        );
    }
);

FlowEmailTimeline.displayName = 'FlowEmailTimeline';
